import { message, notification } from "antd";

/** SurveyWebSPA-specific prefix so storage does not clash with other apps on the same origin. */
const SESSION_DRAFT_PREFIX = "bokss.surveyWebSpa.sessionDraft.";

const LS_OUTBOX_KEY = "bokss.surveyWebSpa.failedSubmissions";

export type SurveyFailedSubmissionRecord = {
  id: string;
  context: string;
  scopeKey: string;
  savedAt: string;
  payload: Record<string, unknown>;
  surveyId?: string;
  extra?: Record<string, unknown>;
};

export function isSurveyFailedSubmissionRecord(
  value: unknown
): value is SurveyFailedSubmissionRecord {
  if (value === null || typeof value !== "object") return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.context === "string" &&
    typeof o.scopeKey === "string" &&
    typeof o.savedAt === "string" &&
    o.payload !== null &&
    typeof o.payload === "object" &&
    !Array.isArray(o.payload)
  );
}

function readOutbox(): SurveyFailedSubmissionRecord[] {
  try {
    const raw = localStorage.getItem(LS_OUTBOX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeOutbox(records: SurveyFailedSubmissionRecord[]) {
  try {
    localStorage.setItem(LS_OUTBOX_KEY, JSON.stringify(records));
  } catch (e) {
    console.warn("surveyOfflinePersistence: could not write outbox", e);
  }
}

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function saveSurveyDraftSession(
  key: string,
  data: Record<string, unknown>
) {
  try {
    sessionStorage.setItem(
      SESSION_DRAFT_PREFIX + key,
      JSON.stringify(data)
    );
  } catch (e) {
    console.warn("saveSurveyDraftSession", e);
  }
}

export function loadSurveyDraftSession(
  key: string
): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(SESSION_DRAFT_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function clearSurveyDraftSession(key: string) {
  try {
    sessionStorage.removeItem(SESSION_DRAFT_PREFIX + key);
  } catch {
    /* ignore */
  }
}

const surveyDraftFlushers = new Map<string, () => void>();

export function registerSurveyDraftFlusher(key: string, flush: () => void) {
  surveyDraftFlushers.set(key, flush);
}

export function unregisterSurveyDraftFlusher(key: string) {
  surveyDraftFlushers.delete(key);
}

export function flushSurveyDraftIfRegistered(key: string | undefined) {
  if (key == null || key === "") return;
  surveyDraftFlushers.get(key)?.();
}

function parseResultJsonObject(value: string | undefined): Record<string, unknown> {
  if (value === undefined || value === null || String(value).trim() === "") {
    return {};
  }
  try {
    const parsed = JSON.parse(value);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {
      return parsed as Record<string, unknown>;
    }
    return {};
  } catch {
    return {};
  }
}

function isFilledSurveyAnswerValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean" || typeof value === "number") return true;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }
  return true;
}

function mergeSurveyAnswerRecords(
  base: Record<string, unknown>,
  overlayWhenFilled: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [key, overlayVal] of Object.entries(overlayWhenFilled)) {
    const baseVal = out[key];
    if (
      overlayVal !== null &&
      typeof overlayVal === "object" &&
      !Array.isArray(overlayVal)
    ) {
      const baseInner =
        baseVal !== null &&
        typeof baseVal === "object" &&
        !Array.isArray(baseVal)
          ? (baseVal as Record<string, unknown>)
          : {};
      out[key] = mergeSurveyAnswerRecords(
        baseInner,
        overlayVal as Record<string, unknown>
      );
      continue;
    }
    if (isFilledSurveyAnswerValue(overlayVal)) {
      out[key] = overlayVal;
    }
  }
  return out;
}

function parseBackupResultJson(
  backupResultJson: unknown
): Record<string, unknown> | null {
  if (backupResultJson === undefined || backupResultJson === null) return null;
  if (typeof backupResultJson === "string") {
    const t = backupResultJson.trim();
    if (t === "") return null;
    try {
      const parsed = JSON.parse(backupResultJson);
      if (
        parsed !== null &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        return parsed as Record<string, unknown>;
      }
      return {};
    } catch {
      return {};
    }
  }
  if (
    typeof backupResultJson === "object" &&
    !Array.isArray(backupResultJson)
  ) {
    return backupResultJson as Record<string, unknown>;
  }
  return {};
}

export function surveyAnswerMergedFromBackup(
  currentResultJson: string | undefined,
  backupResultJson: unknown,
  draftStorageKey?: string
): string {
  flushSurveyDraftIfRegistered(draftStorageKey);
  const fromReducer = parseResultJsonObject(currentResultJson);
  const fromDraft =
    draftStorageKey != null && draftStorageKey !== ""
      ? loadSurveyDraftSession(draftStorageKey) ?? {}
      : {};
  const currentCombined = mergeSurveyAnswerRecords(fromReducer, fromDraft);
  const backupParsed = parseBackupResultJson(backupResultJson);
  if (backupParsed === null) {
    return JSON.stringify(currentCombined);
  }
  const merged = mergeSurveyAnswerRecords(currentCombined, backupParsed);
  return JSON.stringify(merged);
}

export function getPendingForScope(
  scopeKey: string
): SurveyFailedSubmissionRecord | null {
  const list = readOutbox();
  const match = list
    .filter((r) => r.scopeKey === scopeKey)
    .sort(
      (a, b) =>
        new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()
    );
  return match[0] ?? null;
}

export function persistFailedSurveySubmission(args: {
  scopeKey: string;
  context: string;
  payload: Record<string, unknown>;
  surveyId?: string;
  extra?: Record<string, unknown>;
}): SurveyFailedSubmissionRecord {
  const record: SurveyFailedSubmissionRecord = {
    id: generateId(),
    context: args.context,
    scopeKey: args.scopeKey,
    savedAt: new Date().toISOString(),
    payload: args.payload,
    surveyId: args.surveyId,
    extra: args.extra,
  };

  const list = readOutbox().filter((r) => r.scopeKey !== args.scopeKey);
  list.push(record);
  writeOutbox(list);

  notification.warning({
    message: "Save failed",
    description:
      "Your answers were kept on this device. Use Restore or Upload JSON below, then try again.",
    duration: 10,
  });

  return record;
}

export function downloadFailedSubmissionBackup(
  record: SurveyFailedSubmissionRecord,
  filename?: string
) {
  const blob = new Blob([JSON.stringify(record, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download =
    filename ??
    `survey-backup-${record.context.replace(/[^a-z0-9]+/gi, "-")}-${record.id}.json`;
  a.click();
  URL.revokeObjectURL(url);
  message.success("Backup downloaded.");
}

export function removePendingForScopeKey(scopeKey: string) {
  const list = readOutbox().filter((r) => r.scopeKey !== scopeKey);
  writeOutbox(list);
}

export function removePendingById(id: string) {
  const list = readOutbox().filter((r) => r.id !== id);
  writeOutbox(list);
}

export function clearSurveySensitiveStorageOnLogout() {
  try {
    localStorage.removeItem(LS_OUTBOX_KEY);
  } catch {
    /* ignore */
  }
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(SESSION_DRAFT_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** Member + survey route scope for the SurveyWebSPA fill page. */
export function buildSurveyWebSpaCreateScopeKey(
  memberId: string,
  surveyId: string
) {
  return `ss-create|${memberId}|${surveyId}`;
}

let onlineListenerRegistered = false;

export function registerSurveyOnlineReconnectHint() {
  if (typeof window === "undefined" || onlineListenerRegistered) return;
  onlineListenerRegistered = true;
  window.addEventListener("online", () => {
    try {
      const raw = localStorage.getItem(LS_OUTBOX_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        message.info(
          "You are back online. Return to this questionnaire to restore or resubmit your backup.",
          6
        );
      }
    } catch {
      /* ignore */
    }
  });
}
